/**
 * Merge adjacent implied activity-log slices for the Settings team report.
 * Firestore rows stay as-is; this is display-only.
 */

export const ACTIVITY_LOG_MERGE_GAP_MS = 5 * 60 * 1000;

export interface ActivityLogEntry {
  id: string;
  source: 'project' | 'lead' | 'crm';
  userId: string;
  userName: string;
  minutes: number;
  startedAt: Date;
  endedAt: Date;
  pageLabel: string;
  entityLabel?: string;
  type: 'implied' | 'explicit';
  note?: string;
}

export type ActivityLogDisplayGroup =
  | { kind: 'single'; entry: ActivityLogEntry }
  | { kind: 'merged'; entries: ActivityLogEntry[] };

function isImpliedAuto(entry: ActivityLogEntry): boolean {
  return entry.type === 'implied' && !entry.note?.trim();
}

function mergeKey(entry: ActivityLogEntry): string {
  return [entry.userId, entry.source, entry.pageLabel, entry.entityLabel || ''].join('|');
}

export function buildActivityLogDisplayGroups(entries: ActivityLogEntry[]): ActivityLogDisplayGroup[] {
  const implied = entries.filter(isImpliedAuto);
  const other = entries.filter((e) => !isImpliedAuto(e));

  const sorted = [...implied].sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
  const chunks: ActivityLogEntry[][] = [];

  for (const entry of sorted) {
    const prev = chunks[chunks.length - 1];
    if (prev) {
      const last = prev[prev.length - 1];
      const gap = entry.startedAt.getTime() - last.endedAt.getTime();
      if (gap <= ACTIVITY_LOG_MERGE_GAP_MS && mergeKey(entry) === mergeKey(last)) {
        prev.push(entry);
        continue;
      }
    }
    chunks.push([entry]);
  }

  const groups: ActivityLogDisplayGroup[] = [];
  for (const arr of chunks) {
    groups.push(arr.length === 1 ? { kind: 'single', entry: arr[0] } : { kind: 'merged', entries: arr });
  }
  for (const entry of other) {
    groups.push({ kind: 'single', entry });
  }

  groups.sort((a, b) => {
    const endA = a.kind === 'single' ? a.entry.endedAt : a.entries[a.entries.length - 1].endedAt;
    const endB = b.kind === 'single' ? b.entry.endedAt : b.entries[b.entries.length - 1].endedAt;
    return endB.getTime() - endA.getTime();
  });

  return groups;
}

export function sumMinutesInActivityGroup(group: ActivityLogDisplayGroup): number {
  if (group.kind === 'single') return group.entry.minutes;
  return group.entries.reduce((sum, entry) => sum + entry.minutes, 0);
}

export function activityGroupStart(group: ActivityLogDisplayGroup): Date {
  return group.kind === 'single' ? group.entry.startedAt : group.entries[0].startedAt;
}

export function activityGroupEnd(group: ActivityLogDisplayGroup): Date {
  return group.kind === 'single'
    ? group.entry.endedAt
    : group.entries[group.entries.length - 1].endedAt;
}
